import { useRef, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/shadcn/tooltip'
import { notify } from '@/lib/notify'
import { readSheetAsRows, type SheetRow } from '@/lib/readSpreadsheet'
import { useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees } from '@/api/generated/company-management-user/company-management-user'
import { useHealthInsuranceConfigs } from '../hooks/useHealthInsuranceConfigs.ts'
import { HealthInsuranceImportDialog } from '../components/HealthInsuranceImportDialog.tsx'

const COL_FIRST_NAME       = 'Vorname'
const COL_LAST_NAME        = 'Nachname'
const COL_INSURANCE_NUMBER = 'Versicherungsnummer'
const REQUIRED_COLS        = [COL_FIRST_NAME, COL_LAST_NAME, COL_INSURANCE_NUMBER]

export interface ImportRow {
  firstName: string
  lastName: string
  healthInsuranceNumber: string
}

function unwrap<T>(data: unknown): T | undefined {
  return ((data as { data?: T })?.data ?? data) as T | undefined
}

interface Props {
  serviceModuleId: number
  companyId: number
  onSuccess?: () => void
}

export function HealthInsuranceImportButton({ serviceModuleId, companyId, onSuccess }: Props) {
  const { t } = useTranslation('service-modules-insurance')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [importRows, setImportRows] = useState<ImportRow[] | null>(null)

  const employeesQuery = useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees(serviceModuleId)
  const employees = useMemo(
    () => unwrap<{ id?: string | null; firstName?: string | null; lastName?: string | null }[]>(employeesQuery.data) ?? [],
    [employeesQuery.data],
  )

  const { healthConfigsByEmployeeId } = useHealthInsuranceConfigs(serviceModuleId)
  const configs = useMemo(() => Array.from(healthConfigsByEmployeeId.values()), [healthConfigsByEmployeeId])

  const handleClick = () => fileInputRef.current?.click()

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''

    let rows
    try {
      rows = await readSheetAsRows(file)
    } catch {
      notify.error(t('insurance.import.readError'))
      return
    }

    if (rows.length === 0) {
      notify.error(t('insurance.import.noData'))
      return
    }

    const headers = Object.keys(rows[0]).map(h => h.toString().trim())
    const missing = REQUIRED_COLS.filter(col => !headers.includes(col))
    if (missing.length > 0) {
      notify.error(t('insurance.import.missingCols', { cols: missing.join(', ') }))
      return
    }

    const parsed: ImportRow[] = rows
      .map((row: SheetRow) => ({
        firstName: String(row[COL_FIRST_NAME] ?? '').trim(),
        lastName: String(row[COL_LAST_NAME] ?? '').trim(),
        healthInsuranceNumber: String(row[COL_INSURANCE_NUMBER] ?? '').trim(),
      }))
      .filter((r: ImportRow) => r.firstName || r.lastName || r.healthInsuranceNumber)

    setImportRows(parsed)
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.csv"
        className="hidden"
        onChange={handleFileChange}
      />
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={handleClick}>
              <Upload className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('insurance.importButton')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <HealthInsuranceImportDialog
        open={importRows !== null}
        onOpenChange={(open) => { if (!open) setImportRows(null) }}
        initialRows={importRows ?? []}
        serviceModuleId={serviceModuleId}
        companyId={companyId}
        employees={employees}
        configs={configs}
        onSuccess={onSuccess}
      />
    </>
  )
}
